import { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import { LoadingSkeleton } from "@/client/components/common/loading-skeleton"
import { useToast } from "@/client/components/common/toast"
import type { Hackathon, HackathonStatus } from "@/lib/supabase/types"
import styles from "./hackathon-page.module.css"

const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
)

const TrophyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9H4.5a2.5 2.5 0 010-5H6"/>
    <path d="M18 9h1.5a2.5 2.5 0 000-5H18"/>
    <path d="M4 22h16"/>
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
    <path d="M18 2H6v7a6 6 0 0012 0V2z"/>
  </svg>
)

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
)

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M22 21v-2a4 4 0 00-3-3.87"/>
    <path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
)

type TabType = "overview" | "challenges" | "rules"

const STATUS_LABELS: Record<HackathonStatus, string> = {
  upcoming: "即将开始",
  registration: "报名进行中",
  ongoing: "比赛进行中",
  judging: "评审中",
  completed: "已结束",
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

function formatPrize(bbp: number): string {
  if (bbp >= 10000) return `¥${Math.round(bbp / 1000)}K`
  return `¥${bbp.toLocaleString()}`
}

function HackathonLoading() {
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.backBtn}><BackIcon /></div>
        <div className={styles.headerTitle}>黑客马拉松</div>
        <div className={styles.headerRight} />
      </div>
      <div className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroContent}>
          <LoadingSkeleton variant="text" width="40%" height={14} />
          <LoadingSkeleton variant="text" width="70%" height={24} style={{ marginTop: 12 }} />
          <LoadingSkeleton variant="text" width="50%" height={14} style={{ marginTop: 8 }} />
        </div>
      </div>
    </div>
  )
}

export default function HackathonPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const toast = useToast()

  const [hackathon, setHackathon] = useState<Hackathon | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>("overview")
  const [registered, setRegistered] = useState(false)

  const fetchData = useCallback(async () => {
    if (!id) return
    const supabase = getBrowserSupabaseClient()
    if (!supabase) { setError("数据库未配置"); setLoading(false); return }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchErr } = await supabase
        .from("hackathons")
        .select("*")
        .eq("id", id)
        .single()

      if (fetchErr || !data) { setError("赛事不存在"); setLoading(false); return }
      setHackathon(data)
    } catch {
      setError("加载失败，请重试")
      toast.error("加载赛事失败")
    } finally {
      setLoading(false)
    }
  }, [id, toast])

  useEffect(() => { fetchData() }, [fetchData])

  function handleRegister() {
    if (!user) {
      toast.info("请先登录")
      navigate("/login")
      return
    }
    setRegistered(true)
    toast.success("报名成功!")
  }

  if (loading) return <HackathonLoading />

  if (error || !hackathon) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}><BackIcon /></button>
          <div className={styles.headerTitle}>黑客马拉松</div>
          <div className={styles.headerRight} />
        </div>
        <div style={{ padding: "60px 16px", textAlign: "center" }}>
          <p style={{ fontSize: 16, color: "var(--t2)", marginBottom: 16 }}>{error || "赛事不存在"}</p>
          <button onClick={fetchData}
            style={{ padding: "10px 24px", borderRadius: 10, border: "1px solid var(--bd)", background: "var(--bg3)", color: "var(--t)", cursor: "pointer", fontSize: 14 }}>
            重新加载
          </button>
        </div>
      </div>
    )
  }

  const isRegistrationOpen = hackathon.status === "registration"
  const prizeText = formatPrize(hackathon.prize_pool_bbp)

  const timeline = [
    { date: hackathon.registration_start, title: "报名开始" },
    { date: hackathon.registration_end, title: "报名截止" },
    { date: hackathon.event_start, title: "比赛开始" },
    { date: hackathon.event_end, title: "比赛结束" },
  ]

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <div className={styles.headerTitle}>黑客马拉松</div>
        <div className={styles.headerRight} />
      </div>

      {/* Hero Section */}
      <div className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroContent}>
          <div className={styles.statusBadge}>
            <span className={styles.statusDot} />
            {STATUS_LABELS[hackathon.status]}
          </div>
          <h1 className={styles.title}>{hackathon.title}</h1>
          {hackathon.description && (
            <p className={styles.subtitle}>{hackathon.description.slice(0, 60)}</p>
          )}

          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.statValue}>{prizeText}</span>
              <span className={styles.statLabel}>总奖金池</span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.statValue}>
                {(() => {
                  const start = new Date(hackathon.event_start)
                  const end = new Date(hackathon.event_end)
                  const hours = Math.round((end.getTime() - start.getTime()) / 3600000)
                  return `${hours}h`
                })()}
              </span>
              <span className={styles.statLabel}>限时挑战</span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.statValue}>{hackathon.current_participants}</span>
              <span className={styles.statLabel}>参赛队伍</span>
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className={styles.infoCards}>
        <div className={styles.infoCard}>
          <div className={styles.infoIcon}><CalendarIcon /></div>
          <div className={styles.infoContent}>
            <span className={styles.infoLabel}>报名时间</span>
            <span className={styles.infoValue}>
              {formatDate(hackathon.registration_start)} - {formatDate(hackathon.registration_end)}
            </span>
          </div>
        </div>
        <div className={styles.infoCard}>
          <div className={styles.infoIcon}><CalendarIcon /></div>
          <div className={styles.infoContent}>
            <span className={styles.infoLabel}>活动时间</span>
            <span className={styles.infoValue}>
              {formatDate(hackathon.event_start)} - {formatDate(hackathon.event_end)}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        {(["overview", "challenges", "rules"] as const).map((tab) => (
          <button
            key={tab}
            className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "overview" ? "赛事概览" : tab === "challenges" ? "赛题列表" : "参赛规则"}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {activeTab === "overview" && (
          <div className={styles.overview}>
            {/* Prize Banner */}
            <div className={styles.prizeBanner}>
              <div className={styles.prizeIcon}><TrophyIcon /></div>
              <div className={styles.prizeContent}>
                <span className={styles.prizeLabel}>奖金池</span>
                <span className={styles.prizeValue}>{prizeText}</span>
              </div>
              <div className={styles.prizeBadge}>总奖金</div>
            </div>

            {/* Description */}
            {hackathon.description && (
              <div style={{ padding: "0 16px 16px" }}>
                <h3 className={styles.sectionTitle}>赛事介绍</h3>
                <p style={{ fontSize: 14, color: "var(--t2)", lineHeight: 1.6 }}>
                  {hackathon.description}
                </p>
              </div>
            )}

            {/* Schedule */}
            <div className={styles.schedule}>
              <h3 className={styles.sectionTitle}>赛程安排</h3>
              <div className={styles.timeline}>
                {timeline.map((item) => (
                  <div key={item.title} className={styles.timelineItem}>
                    <div className={styles.timelineDot} />
                    <div className={styles.timelineContent}>
                      <span className={styles.timelineDate}>{formatDateTime(item.date)}</span>
                      <span className={styles.timelineTitle}>{item.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "challenges" && (
          <div className={styles.challenges}>
            <h3 className={styles.sectionTitle}>赛题信息</h3>
            <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--t3)" }}>
              {hackathon.status === "upcoming" || hackathon.status === "registration"
                ? "赛题将在比赛开始时公布"
                : "赛题信息暂未录入"}
            </div>
          </div>
        )}

        {activeTab === "rules" && (
          <div className={styles.rules}>
            <h3 className={styles.sectionTitle}>参赛须知</h3>
            {hackathon.rules ? (
              <div style={{ padding: "0 16px", whiteSpace: "pre-wrap", fontSize: 14, color: "var(--t2)", lineHeight: 1.8 }}>
                {hackathon.rules}
              </div>
            ) : (
              <div className={styles.ruleList}>
                <div className={styles.ruleItem}>
                  <div className={styles.ruleIcon}><CheckIcon /></div>
                  <div className={styles.ruleText}>
                    <span className={styles.ruleTitle}>团队要求</span>
                    <span className={styles.ruleDesc}>鼓励跨专业协作，团队人数不限</span>
                  </div>
                </div>
                <div className={styles.ruleItem}>
                  <div className={styles.ruleIcon}><CheckIcon /></div>
                  <div className={styles.ruleText}>
                    <span className={styles.ruleTitle}>作品形式</span>
                    <span className={styles.ruleDesc}>可运行的产品原型或技术方案</span>
                  </div>
                </div>
                <div className={styles.ruleItem}>
                  <div className={styles.ruleIcon}><CheckIcon /></div>
                  <div className={styles.ruleText}>
                    <span className={styles.ruleTitle}>开源要求</span>
                    <span className={styles.ruleDesc}>获奖作品需开源</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Action */}
      <div className={styles.bottomAction}>
        <div className={styles.actionInfo}>
          <UsersIcon />
          <span>已有 {hackathon.current_participants} 支队伍报名</span>
        </div>
        <button
          className={`${styles.registerBtn} ${registered || !isRegistrationOpen ? styles.registered : ""}`}
          onClick={handleRegister}
          disabled={registered || !isRegistrationOpen}
        >
          {registered ? "已报名" : !isRegistrationOpen ? STATUS_LABELS[hackathon.status] : "立即报名"}
        </button>
      </div>
    </div>
  )
}
